import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Box
} from '@mui/material';
import { Close } from '@mui/icons-material';
import GetQuoteMethodSelector from './GetQuoteMethodSelector';
import BOQUploadStep from './BOQUploadStep';
import MaterialRequestForm from './MaterialRequestForm';
import MerchantSelector from './MerchantSelector';
import QuoteRequestConfirmation from './QuoteRequestConfirmation';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';

const QuoteRequestWizard = ({ open, onClose, project, preSelectedMerchantId, initialMaterials = null }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // When the caller supplies materials (e.g. from the BOQ page), skip the
  // method-selection step and land straight on the prefilled material form.
  React.useEffect(() => {
    if (open && initialMaterials && Object.keys(initialMaterials).length > 0) {
      setRequestData({ method: 'material_form' });
      setActiveStep(1);
    }
  }, [open, initialMaterials]);

  const steps = [
    'Choose Method',
    'Enter Details',
    'Select Suppliers',
    'Confirm & Send'
  ];

  const handleMethodSelect = (method) => {
    setActiveStep(1);
    setRequestData({ method });
  };

  const handleDetailsComplete = (data) => {
    console.log('handleDetailsComplete received data:', data);
    console.log('materialRequest in data:', data.materialRequest);
    setRequestData(prev => {
      const merged = { ...prev, ...data };
      console.log('Merged requestData:', merged);
      console.log('materialRequest in merged:', merged.materialRequest);
      return merged;
    });
    setActiveStep(2);
  };

  const handleMerchantsSelected = (data) => {
    try {
      console.log('Merchants selected, data received:', data);
      if (!data || !data.selectedMerchantIds || data.selectedMerchantIds.length === 0) {
        console.error('No merchants selected');
        return;
      }
      setRequestData(prev => ({ ...prev, ...data }));
      setActiveStep(3);
    } catch (error) {
      console.error('Error in handleMerchantsSelected:', error);
      setError('Failed to proceed. Please try again.');
    }
  };

  const handleSendRequest = async () => {
    console.log('handleSendRequest called');
    console.log('requestData:', requestData);
    console.log('project:', project);
    
    try {
      setLoading(true);
      setError('');


      // Prepare request data
      // deliveryAddress should be a JSON object, not a string
      let deliveryAddress = requestData?.deliveryAddress;
      
      // If deliveryAddress is an object but missing address field, add it
      if (deliveryAddress && typeof deliveryAddress === 'object') {
        // If it has street/city but no address, create address from those fields
        if (!deliveryAddress.address) {
          const addressParts = [];
          if (deliveryAddress.street) addressParts.push(deliveryAddress.street);
          if (deliveryAddress.suburb) addressParts.push(deliveryAddress.suburb);
          if (deliveryAddress.city) addressParts.push(deliveryAddress.city);
          if (deliveryAddress.province) addressParts.push(deliveryAddress.province);
          
          deliveryAddress.address = addressParts.length > 0 
            ? addressParts.join(', ') 
            : (project?.location || '');
        }
        // Ensure we have at least city or address
        if (!deliveryAddress.city && deliveryAddress.address) {
          deliveryAddress.city = deliveryAddress.address.split(',')[0]?.trim() || '';
        }
      } else if (!deliveryAddress && project?.location) {
        // If we only have a location string, convert it to an object
        deliveryAddress = {
          address: project.location,
          city: project.location.split(',')[0]?.trim() || '',
          country: 'Zimbabwe'
        };
      } else if (typeof deliveryAddress === 'string') {
        // Convert string to object if needed
        deliveryAddress = {
          address: deliveryAddress,
          city: deliveryAddress.split(',')[0]?.trim() || '',
          country: 'Zimbabwe'
        };
      } else if (!deliveryAddress) {
        // Fallback to project location
        deliveryAddress = {
          address: project?.location || '',
          city: project?.location?.split(',')[0]?.trim() || '',
          country: 'Zimbabwe'
        };
      }

      const requestPayload = {
        projectId: project?.id,
        requestType: requestData?.method || requestData?.requestType || 'material_form',
        deliveryAddress: deliveryAddress || { address: project?.location || '', city: '', country: 'Zimbabwe' },
        selectedMerchantIds: requestData?.selectedMerchantIds || [],
        ...(requestData?.method === 'boq_upload' && {
          boqFileUrl: requestData?.boqFileUrl,
          boqFileName: requestData?.boqFileName,
          boqFileType: requestData?.boqFileType,
          quoteEverything: requestData?.quoteEverything !== false,
          selectedItems: requestData?.selectedItems
        }),
        ...(requestData?.method === 'material_form' && {
          materialCategories: requestData?.materialCategories || [],
          materialItems: requestData?.materialItems || [],
          materialRequest: requestData?.materialRequest || null,
          deliveryTimeframe: requestData?.deliveryTimeframe,
          deliveryDate: requestData?.deliveryDate,
          specialRequirements: requestData?.specialRequirements,
          additionalNotes: requestData?.additionalNotes || requestData?.specialRequirements
        })
      };

      console.log('Sending quote request with payload:', requestPayload);
      console.log('materialRequest in payload:', requestPayload.materialRequest);
      console.log('materialRequest type:', typeof requestPayload.materialRequest);
      if (requestPayload.materialRequest) {
        console.log('materialRequest stringified:', JSON.stringify(requestPayload.materialRequest));
      }

      // Validate required fields
      if (!requestPayload.projectId) {
        setError('Project ID is required');
        setLoading(false);
        return;
      }

      if (!requestPayload.selectedMerchantIds || requestPayload.selectedMerchantIds.length === 0) {
        setError('Please select at least one merchant');
        setLoading(false);
        return;
      }

      // Validate delivery address - check for address field or city field
      if (!requestPayload.deliveryAddress) {
        setError('Delivery address is required');
        setLoading(false);
        return;
      }
      
      if (typeof requestPayload.deliveryAddress === 'object') {
        // Check if we have at least an address or city
        const hasAddress = requestPayload.deliveryAddress.address && 
                          requestPayload.deliveryAddress.address.trim().length > 0;
        const hasCity = requestPayload.deliveryAddress.city && 
                       requestPayload.deliveryAddress.city.trim().length > 0;
        const hasStreet = requestPayload.deliveryAddress.street && 
                         requestPayload.deliveryAddress.street.trim().length > 0;
        
        if (!hasAddress && !hasCity && !hasStreet) {
          console.error('Delivery address validation failed:', requestPayload.deliveryAddress);
          setError('Delivery address must include at least a street, city, or address field');
          setLoading(false);
          return;
        }
      } else if (typeof requestPayload.deliveryAddress === 'string') {
        if (!requestPayload.deliveryAddress.trim()) {
          setError('Delivery address cannot be empty');
          setLoading(false);
          return;
        }
      }

      // Add timeout to prevent hanging
      const response = await api.post(
        '/merchant-quotes/request',
        requestPayload,
        { timeout: 30000 } // 30 second timeout
      );

      console.log('Quote request response:', response.data);

      if (response.data.success) {
        // Success - close wizard and show success message
        onClose(true); // Pass true to indicate success
      } else {
        const errorMsg = response.data.error || response.data.message || 'Failed to send quote request';
        setError(errorMsg);
        if (response.data.details) {
          console.error('Validation errors:', response.data.details);
        }
      }
    } catch (err) {
      console.error('Send quote request error:', err);
      
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. Please check your connection and try again.');
      } else if (err.response) {
        // Server responded with error
        const errorMsg = err.response.data?.error || 
                        err.response.data?.message || 
                        `Server error: ${err.response.status}`;
        setError(errorMsg);
        
        if (err.response.data?.details) {
          console.error('Validation errors:', err.response.data.details);
          const validationErrors = err.response.data.details
            .map(d => d.msg || d.message)
            .join(', ');
          setError(`${errorMsg}: ${validationErrors}`);
        }
      } else if (err.request) {
        // Request made but no response
        setError('No response from server. Please check your connection and try again.');
      } else {
        setError('Failed to send quote request. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setError('');
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setRequestData(null);
    setError('');
    onClose(false);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <GetQuoteMethodSelector
            project={project}
            onSelectMethod={handleMethodSelect}
            onBack={handleClose}
          />
        );

      case 1:
        return requestData?.method === 'boq_upload' ? (
          <BOQUploadStep
            project={project}
            onNext={handleDetailsComplete}
            onBack={handleBack}
            onCancel={handleClose}
          />
        ) : (
          <MaterialRequestForm
            project={project}
            onNext={handleDetailsComplete}
            onBack={handleBack}
            onCancel={handleClose}
            initialItems={initialMaterials}
          />
        );

      case 2:
        return (
          <MerchantSelector
            project={project}
            requestData={requestData}
            onNext={handleMerchantsSelected}
            onBack={handleBack}
            onCancel={handleClose}
            preSelectedMerchantId={preSelectedMerchantId}
          />
        );

      case 3:
        return (
          <QuoteRequestConfirmation
            project={project}
            requestData={requestData}
            onSend={handleSendRequest}
            onBack={handleBack}
            onCancel={handleClose}
            loading={loading}
            error={error}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogCloseButton onClick={handleClose} />
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Stepper activeStep={activeStep} sx={{ flex: 1 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ overflowY: 'auto' }}>
        {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteRequestWizard;

